import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import '../components/AdminLayout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type Tab = 'usage' | 'posts' | 'controls' | 'limits';

interface UserDetail {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: string;
  plan: string | null;
  productEnabled: boolean;
  subscriptionPlanId: string | null;
  subscriptionPlanName?: string | null;
  subscriptionEndsAt?: string | null;
  subscriptionRenewedAt?: string | null;
  createdAt: string;
  limits: {
    slideshowCurrent: number; slideshowMax: number | null;
    shortsCurrent: number; shortsMax: number | null;
    subtitleCurrent: number; subtitleMax: number | null;
    youtubePostsCurrent: number; youtubePostsMax: number | null;
    tiktokPostsCurrent: number; tiktokPostsMax: number | null;
    analyticsNewVideoRefreshHrs: number | null;
    analyticsOldVideoRefreshHrs: number | null;
    analyticsAccountRefreshHrs: number | null;
  } | null;
}

interface UsageStats {
  projects_count: string;
  videos_count: string;
  shorts_count: string;
  published_count: string;
}

interface Post {
  id: string;
  overall_status: string;
  created_at: string;
  title: string | null;
  platforms: string[] | null;
}

interface ActivePlan {
  id: string;
  name: string;
}

function getAvailableCredits(current: number, max: number | null): number | null {
  if (max == null) return null;
  return Math.max(max - current, 0);
}

function formatOptionalDate(value?: string | null): string {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available';
  return parsed.toLocaleDateString();
}

function getCurrentPlanLabel(user: UserDetail): string {
  if (user.subscriptionPlanName && user.subscriptionPlanName.trim()) {
    return user.subscriptionPlanName;
  }
  if (user.plan && user.plan.trim()) {
    return user.plan;
  }
  return 'Pay As You Go';
}

const AdminUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [tab, setTab] = useState<Tab>('usage');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Controls edit state
  const [ctrlEnabled, setCtrlEnabled] = useState(true);
  const [ctrlSubscriptionPlanId, setCtrlSubscriptionPlanId] = useState('');
  const [activePlans, setActivePlans] = useState<ActivePlan[]>([]);

  // Limits edit state
  const [limEdits, setLimEdits] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      fetch(`${API_BASE}/api/admin/users/${userId}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API_BASE}/api/admin/users/${userId}/usage`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API_BASE}/api/admin/users/${userId}/posts`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API_BASE}/api/admin/pricing/plans/active`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([userDetail, usageStats, postsData, activePlansData]) => {
      setUser(userDetail);
      setUsage(usageStats);
      setPosts(postsData.posts || []);
      setCtrlEnabled(userDetail.productEnabled);
      setCtrlSubscriptionPlanId(userDetail.subscriptionPlanId || '');
      setActivePlans((activePlansData.plans || []).map((plan: { id: string; name: string }) => ({ id: plan.id, name: plan.name })));
      if (userDetail.limits) {
        const l = userDetail.limits;
        setLimEdits({
          slideshowMax: l.slideshowMax != null ? String(l.slideshowMax) : '',
          shortsMax: l.shortsMax != null ? String(l.shortsMax) : '',
          subtitleMax: l.subtitleMax != null ? String(l.subtitleMax) : '',
          youtubePostsMax: l.youtubePostsMax != null ? String(l.youtubePostsMax) : '',
          tiktokPostsMax: l.tiktokPostsMax != null ? String(l.tiktokPostsMax) : '',
          analyticsNewVideoRefreshHrs: l.analyticsNewVideoRefreshHrs != null ? String(l.analyticsNewVideoRefreshHrs) : '',
          analyticsOldVideoRefreshHrs: l.analyticsOldVideoRefreshHrs != null ? String(l.analyticsOldVideoRefreshHrs) : '',
          analyticsAccountRefreshHrs: l.analyticsAccountRefreshHrs != null ? String(l.analyticsAccountRefreshHrs) : '',
        });
      }
    }).catch(() => setError('Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  const saveControls = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/controls`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productEnabled: ctrlEnabled,
          subscriptionPlanId: ctrlSubscriptionPlanId || null,
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setUser(updated);
    } catch { setError('Failed to save controls'); }
    finally { setSaving(false); }
  };

  const saveLimits = async () => {
    setSaving(true); setError(null);
    const toNum = (v: string) => v ? parseInt(v, 10) : null;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/limits`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slideshowMax: toNum(limEdits.slideshowMax),
          shortsMax: toNum(limEdits.shortsMax),
          subtitleMax: toNum(limEdits.subtitleMax),
          youtubePostsMax: toNum(limEdits.youtubePostsMax),
          tiktokPostsMax: toNum(limEdits.tiktokPostsMax),
          analyticsNewVideoRefreshHrs: toNum(limEdits.analyticsNewVideoRefreshHrs),
          analyticsOldVideoRefreshHrs: toNum(limEdits.analyticsOldVideoRefreshHrs),
          analyticsAccountRefreshHrs: toNum(limEdits.analyticsAccountRefreshHrs),
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setUser(updated);
    } catch { setError('Failed to save limits'); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="admin-loading">Loading…</div></AdminLayout>;
  if (!user) return <AdminLayout><div className="admin-error">User not found. <Link to="/admin/users">Back</Link></div></AdminLayout>;

  const currentPlanLabel = getCurrentPlanLabel(user);
  const endDateLabel = formatOptionalDate(user.subscriptionEndsAt);
  const renewedOnLabel = formatOptionalDate(user.subscriptionRenewedAt);

  return (
    <AdminLayout>
      <div className="admin-back-link-wrap">
        <Link to="/admin/users" className="admin-back-link">← Users</Link>
      </div>
      <div className="admin-page-header admin-page-header--back">
        <div className="admin-page-title-row">
          <AdminBackButton />
          <h1 className="admin-page-title">{user.name || user.email}</h1>
        </div>
        <p className="admin-page-subtitle admin-page-subtitle--data">{user.email} · joined {new Date(user.createdAt).toLocaleDateString()}</p>
      </div>
      {error && <div className="admin-error">{error}</div>}

      <div className="admin-card admin-card-spaced">
        <h3 className="admin-section-title">Subscription</h3>
        <div className="admin-stack admin-stack-gap-12">
          <div className="admin-row-between">
            <span className="admin-cell-muted">Current Plan</span>
            <span className="admin-cell-primary">{currentPlanLabel}</span>
          </div>
          <div className="admin-row-between">
            <span className="admin-cell-muted">End Date</span>
            <span className={endDateLabel === 'Not available' ? 'admin-cell-muted' : 'admin-cell-primary'}>
              {endDateLabel}
            </span>
          </div>
          <div className="admin-row-between">
            <span className="admin-cell-muted">Next Renewal at</span>
            <span className={renewedOnLabel === 'Not available' ? 'admin-cell-muted' : 'admin-cell-primary'}>
              {renewedOnLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="admin-tab-bar admin-tab-bar-spaced">
        {(['usage', 'posts', 'controls', 'limits'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`admin-tab-btn ${tab === t ? 'active' : ''}`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Usage tab */}
      {tab === 'usage' && usage && (
        <div className="admin-card">
          <div className="admin-stats-grid">
            {[
              { label: 'Projects', value: usage.projects_count },
              { label: 'Videos', value: usage.videos_count },
              { label: 'Shorts', value: usage.shorts_count },
              { label: 'Published', value: usage.published_count },
            ].map(stat => (
              <div key={stat.label} className="admin-stat-card">
                <div className="admin-stat-value">{stat.value}</div>
                <div className="admin-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
          {user.limits && (
            <>
              <hr className="admin-divider" />
              <h3 className="admin-section-title">Available Credits</h3>
              <table className="admin-table">
                <thead><tr><th>Feature</th><th>Used</th><th>Max</th><th>Available</th></tr></thead>
                <tbody>
                  {[
                    { label: 'Slideshows', current: user.limits.slideshowCurrent, max: user.limits.slideshowMax },
                    { label: 'Shorts', current: user.limits.shortsCurrent, max: user.limits.shortsMax },
                    { label: 'Subtitles', current: user.limits.subtitleCurrent, max: user.limits.subtitleMax },
                    { label: 'YouTube Posts', current: user.limits.youtubePostsCurrent, max: user.limits.youtubePostsMax },
                    { label: 'TikTok Posts', current: user.limits.tiktokPostsCurrent, max: user.limits.tiktokPostsMax },
                  ].map(row => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{row.current}</td>
                      <td>{row.max != null ? row.max : <span className="admin-cell-muted">Unlimited</span>}</td>
                      <td>{getAvailableCredits(row.current, row.max) != null ? getAvailableCredits(row.current, row.max) : <span className="admin-cell-muted">Unlimited</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Posts tab */}
      {tab === 'posts' && (
        <div className="admin-card">
          <table className="admin-table">
            <thead><tr><th>Title</th><th>Status</th><th>Platforms</th><th>Date</th></tr></thead>
            <tbody>
              {posts.length === 0 && <tr><td colSpan={4} className="admin-cell-empty">No posts.</td></tr>}
              {posts.map(p => (
                <tr key={p.id}>
                  <td className="admin-cell-primary">{p.title || '—'}</td>
                  <td><span className="admin-badge admin-badge-active">{p.overall_status}</span></td>
                  <td className="admin-cell-small">{p.platforms?.join(', ') || '—'}</td>
                  <td className="admin-cell-muted">{new Date(p.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Controls tab */}
      {tab === 'controls' && (
        <div className="admin-card admin-card-narrow">
          <div className="admin-stack admin-stack-gap-16">
            <label className="admin-field-label">Product Access
              <select className="admin-input admin-input-full"
                value={ctrlEnabled ? 'true' : 'false'} onChange={e => setCtrlEnabled(e.target.value === 'true')}>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </label>
            <label className="admin-field-label">Subscription Plan
              <select
                className="admin-input admin-input-full"
                value={ctrlSubscriptionPlanId}
                onChange={(e) => setCtrlSubscriptionPlanId(e.target.value)}
              >
                <option value="">Pay As You Go</option>
                {activePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
            </label>
            <div className="admin-actions-right">
              <button className="admin-btn admin-btn-primary" disabled={saving} onClick={saveControls}>
                {saving ? 'Saving…' : 'Save Controls'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Limits tab */}
      {tab === 'limits' && (
        <div className="admin-card admin-card-narrow">
          <div className="admin-stack admin-stack-gap-14">
            {[
              { key: 'slideshowMax', label: 'Slideshow Max / Month' },
              { key: 'shortsMax', label: 'Shorts Max / Month' },
              { key: 'subtitleMax', label: 'Subtitle Max / Month' },
              { key: 'youtubePostsMax', label: 'YouTube Posts Max / Month' },
              { key: 'tiktokPostsMax', label: 'TikTok Posts Max / Month' },
              { key: 'analyticsNewVideoRefreshHrs', label: 'New Video Analytics Refresh (hrs)' },
              { key: 'analyticsOldVideoRefreshHrs', label: 'Old Video Analytics Refresh (hrs)' },
              { key: 'analyticsAccountRefreshHrs', label: 'Account Analytics Refresh (hrs)' },
            ].map(f => (
              <label key={f.key} className="admin-field-label">{f.label}
                <input className="admin-input admin-input-full" type="number" min="0" placeholder="Unlimited / global default"
                  value={limEdits[f.key] ?? ''}
                  onChange={e => setLimEdits(prev => ({ ...prev, [f.key]: e.target.value }))} />
              </label>
            ))}
            <div className="admin-actions-right admin-mt-8">
              <button className="admin-btn admin-btn-primary" disabled={saving} onClick={saveLimits}>
                {saving ? 'Saving…' : 'Save Limits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUserDetail;
